import { useMemo, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { CtaBand } from "@/components/CtaBand";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";
import { MapPin, Search, ArrowRight, ShieldAlert, SlidersHorizontal } from "lucide-react";

export const Hospitals = () => {
    const {  t, tHtml, site: siteData } = useLang();
    const { HOSPITALS, HOSPITAL_TYPES, SPECIALTIES } = siteData;
    usePageMeta(
        t("meta.hospitals.title", "Hospitals"),
        t("meta.hospitals.desc", "Explore the UniversHeal partner hospital directory for Kerala — searchable by specialty, location and hospital type. Demo profiles awaiting verification.")
    );
    const [query, setQuery] = useState("");
    const [spec, setSpec] = useState("");
    const [loc, setLoc] = useState("");
    const [type, setType] = useState("");

    const locations = useMemo(
        () => [...new Set(HOSPITALS.map((h) => h.location))],
        []
    );

    const filtered = HOSPITALS.filter((h) => {
        const q = query.trim().toLowerCase();
        const matchesQuery =
            !q ||
            h.name.toLowerCase().includes(q) ||
            h.location.toLowerCase().includes(q) ||
            h.desc.toLowerCase().includes(q);
        const matchesSpec = !spec || h.specialties.includes(spec);
        const matchesLoc = !loc || h.location === loc;
        const matchesType = !type || h.type === type;
        return matchesQuery && matchesSpec && matchesLoc && matchesType;
    });

    const clearFilters = () => {
        setQuery("");
        setSpec("");
        setLoc("");
        setType("");
    };

    return (
        <>
            <PageHero
                eyebrow={t("hospitals.eyebrow", "Healthcare Providers")}
                title={t("hospitals.title1", "Healthcare providers you can trust.")}
                intro={t("hospitals.intro", "UniversHeal works with healthcare providers across India — hospitals, specialty centres and, over time, verified Ayurveda and wellness centres. The network is being built carefully: every provider is reviewed before it is presented to any patient, starting in Kerala.")}
            />

            <section className="pb-10">
                <div className="container-site">
                    <Reveal>
                        <div
                            data-testid="hospital-demo-notice"
                            className="flex items-start gap-3 bg-sage/70 border border-pine/15 rounded-xl px-5 py-4"
                        >
                            <ShieldAlert className="w-5 h-5 text-pine shrink-0 mt-0.5" />
                            <p className="text-xs sm:text-sm leading-relaxed text-charcoal/80">
                                <strong>Demonstration directory.</strong> The
                                {t("hospitals.text1", "profiles below are clearly-marked placeholders awaiting verification — no hospital is currently confirmed as a UniversHeal partner, and no accreditation is claimed. Verified profiles will replace these before launch.")}
                            </p>
                        </div>
                    </Reveal>

                    {/* Filter bar */}
                    <Reveal delay={0.05}>
                        <div
                            data-testid="hospital-filters"
                            className="mt-8 bg-white border border-pine/10 rounded-2xl p-5 sm:p-6 grid gap-4 md:grid-cols-4"
                        >
                            <div>
                                <label htmlFor="hospital-search" className="label-field">
                                    Search,
                                </label>
                                <div className="relative">
                                    <Search className="w-4 h-4 text-moss absolute left-4 top-1/2 -translate-y-1/2" />
                                    <input
                                        id="hospital-search"
                                        data-testid="hospital-search"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        className="input-field !pl-10"
                                        placeholder="Search hospitals, cities…"
                                        type="search"
                                    />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="hospital-filter-specialty" className="label-field">
                                    Specialty,
                                </label>
                                <select
                                    id="hospital-filter-specialty"
                                    data-testid="hospital-filter-specialty"
                                    value={spec}
                                    onChange={(e) => setSpec(e.target.value)}
                                    className="input-field"
                                >
                                    <option value="">All specialties</option>
                                    {SPECIALTIES.map((s) => (
                                        <option key={s.slug} value={s.name}>
                                            {s.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="hospital-filter-location" className="label-field">
                                    Location,
                                </label>
                                <select
                                    id="hospital-filter-location"
                                    data-testid="hospital-filter-location"
                                    value={loc}
                                    onChange={(e) => setLoc(e.target.value)}
                                    className="input-field"
                                >
                                    <option value="">All locations</option>
                                    {locations.map((l) => (
                                        <option key={l} value={l}>
                                            {l}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="hospital-filter-type" className="label-field">
                                    {t("hospitals.text5", "Hospital type")}
                                </label>
                                <select
                                    id="hospital-filter-type"
                                    data-testid="hospital-filter-type"
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                    className="input-field"
                                >
                                    <option value="">All types</option>
                                    {HOSPITAL_TYPES.map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </Reveal>

                    <div className="mt-6 flex items-center justify-between">
                        <p
                            data-testid="hospital-result-count"
                            className="text-sm text-moss flex items-center gap-2"
                        >
                            <SlidersHorizontal className="w-4 h-4" />
                            {filtered.length}{" "}
                            {filtered.length === 1 ? "profile" : "profiles"} found
                        </p>
                        {(query || spec || loc || type) && (
                            <button
                                type="button"
                                data-testid="hospital-clear-filters"
                                onClick={clearFilters}
                                className="text-sm font-semibold text-pine underline underline-offset-4 hover:text-forest"
                            >
                                {t("hospitals.text6", "Clear filters")}
                            </button>
                        )}
                    </div>

                    {/* Cards */}
                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hospital-grid">
                        {filtered.map((h, i) => (
                            <Reveal key={h.slug} delay={Math.min(i * 0.05, 0.3)} className="h-full">
                                <article
                                    data-testid={`hospital-card-${h.slug}`}
                                    className="h-full bg-white border border-pine/10 rounded-2xl overflow-hidden card-lift flex flex-col"
                                >
                                    <div className="relative h-44 overflow-hidden">
                                        <img
                                            src={IMG[h.image]}
                                            alt={`Interior of ${h.name}, ${h.location}`}
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                            loading="lazy"
                                        />
                                        <span className="absolute top-3 right-3 bg-ivory/95 text-[10px] font-bold uppercase tracking-widest2 text-moss px-2.5 py-1.5 rounded-md border border-pine/10">
                                            {t("hospitals.text7", "Demo profile")}
                                        </span>
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <p className="text-[11px] font-bold uppercase tracking-widest2 text-natural">
                                            {h.type}
                                        </p>
                                        <h2 className="mt-2 font-serif font-medium text-2xl text-forest">
                                            {h.name}
                                        </h2>
                                        <p className="mt-1.5 text-sm text-moss flex items-center gap-1.5">
                                            <MapPin className="w-3.5 h-3.5" />
                                            {h.location}
                                        </p>
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {h.specialties.map((s) => (
                                                <span
                                                    key={s}
                                                    className="text-[11px] font-semibold text-pine bg-sage px-2.5 py-1 rounded-md"
                                                >
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                        <p className="mt-4 text-sm leading-relaxed text-moss line-clamp-2 flex-1">
                                            {h.desc}
                                        </p>
                                        <p className="mt-3 text-xs text-moss/70">
                                            Accreditation: {h.accreditation}
                                        </p>
                                        <Link
                                            to={`/hospitals/${h.slug}`}
                                            data-testid={`hospital-view-${h.slug}`}
                                            className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-forest hover:text-pine transition-colors group"
                                        >
                                            {t("hospitals.text8", "View Hospital")}
                                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                                        </Link>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>

                    {filtered.length === 0 && (
                        <div
                            data-testid="hospital-empty-state"
                            className="mt-6 bg-white/70 border border-dashed border-pine/30 rounded-2xl px-8 py-16 text-center"
                        >
                            <p className="font-serif italic text-2xl text-forest">
                                {t("hospitals.text9", "No profiles match your search.")}
                            </p>
                            <p className="mt-3 text-sm text-moss">
                                {t("hospitals.text10", "Try clearing a filter — or tell us what you are looking for and we will help directly.")}
                            </p>
                            <button
                                type="button"
                                data-testid="hospital-empty-clear"
                                onClick={clearFilters}
                                className="btn-secondary mt-8"
                            >
                                {t("hospitals.text11", "Clear filters")}
                            </button>
                        </div>
                    )}
                </div>
            </section>

            <CtaBand
                headline={t("hospitals.headline", "Looking for a hospital for a specific case?")}
                text={t("hospitals.text", "Tell us about your needs — we will coordinate providers that genuinely fit your case and preferences.")}
            />
        </>
    );
};
